import { lessonList, letters } from "@repo/hindi-shared";
import { useSyncExternalStore } from "react";

import { navigate } from "../router";
import { loadState } from "../state/store";
import { getSnapshot, subscribe } from "../state/syncStatus";
import { DualLabel } from "../ui/DualLabel";
import { GlyphTile } from "../ui/GlyphTile";
import { InkButton } from "../ui/InkButton";
import { RubricNumeral } from "../ui/RubricNumeral";
import { Shirorekha } from "../ui/Shirorekha";

const FOOTER_LINKS: { hi: string; en: string; path: string }[] = [
  { hi: "वर्णमाला", en: "Varnamala", path: "/varnamala" },
  { hi: "पाठ", en: "Lessons", path: "/lessons" },
  { hi: "अभ्यास", en: "Drills", path: "/drills" },
  { hi: "सेटिंग्स", en: "Settings", path: "/settings" },
];

const CONSONANTS = letters.filter((letter) => letter.kind === "consonant");

/**
 * Today — the home screen.
 *
 * A day's status at a glance (due count + next lesson), the single
 * sindoor-rubricated call to practice, a varnamala mini-strip that darkens
 * as consonants are learned (ink once `reps > 0`, faint otherwise), and
 * quiet footer links to the rest of the app (still stubbed surfaces).
 */
export function Today() {
  const store = loadState();
  const now = Date.now();
  const dueCount = Object.values(store.reviews).filter(
    (r) => r.dueAt <= now,
  ).length;

  const { streak } = useSyncExternalStore(subscribe, getSnapshot);

  const completed = new Set(store.completedLessonIds);
  const nextLesson = lessonList.find((lesson) => !completed.has(lesson.id));

  function handleStart(): void {
    navigate("/session");
  }

  return (
    <main className="paper-grain flex min-h-dvh flex-col items-center px-6 py-12">
      <section className="w-full max-w-md">
        <Shirorekha className="pt-4">
          {/* English-led eyebrow with a small Devanagari echo — the
              learner can't yet read Devanagari, so chrome leads in
              English. Echo never letter-spaced (breaks conjunct shaping). */}
          <DualLabel
            en="Hindi"
            hi="हिन्दी"
            className="text-xs text-ink-soft uppercase"
          />
          <h1 className="mt-4 font-display text-6xl leading-[0.95] text-ink">
            <DualLabel en="Today's practice" hi="आज का रियाज़" />
          </h1>
          {streak !== null && streak >= 1 && (
            <p className="mt-2 font-latin text-base text-ink-soft">
              <span lang="en">Streak</span> — <RubricNumeral value={streak} />{" "}
              <span lang="en">days</span>
            </p>
          )}
        </Shirorekha>

        <div className="mt-8">
          <p className="font-latin text-lg text-ink">
            <RubricNumeral value={dueCount} />{" "}
            <span lang="en">reviews due today</span>
          </p>
          {nextLesson !== undefined && (
            <p className="mt-2 font-latin text-base text-ink-soft">
              <span lang="en">Next lesson</span> —{" "}
              <DualLabel
                variant="inline"
                en={nextLesson.titleEn}
                hi={nextLesson.titleHi}
              />
            </p>
          )}
        </div>

        <div className="mt-8">
          <InkButton variant="sindoor" onClick={handleStart}>
            <DualLabel variant="inline" en="Start" hi="शुरू करें" />
          </InkButton>
        </div>

        <div className="mt-12">
          <DualLabel
            en="Varnamala"
            hi="वर्णमाला"
            className="text-xs text-ink-soft uppercase"
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {CONSONANTS.map((letter) => {
              const learned = (store.reviews[letter.id]?.reps ?? 0) > 0;
              return (
                <div
                  key={letter.id}
                  className={learned ? "opacity-100" : "opacity-40"}
                >
                  <GlyphTile glyph={letter.glyph} size="sm" disabled />
                </div>
              );
            })}
          </div>
        </div>

        <footer className="mt-16 flex flex-wrap gap-x-6 gap-y-2 border-t border-rule pt-4">
          {FOOTER_LINKS.map((link) => (
            <button
              key={link.path}
              type="button"
              onClick={() => navigate(link.path)}
              className="font-latin text-sm text-ink-soft transition-colors duration-150 hover:text-sindoor"
            >
              <DualLabel variant="inline" en={link.en} hi={link.hi} />
            </button>
          ))}
        </footer>
      </section>
    </main>
  );
}
